/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { addThrottledEvent, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { createTabStateInput } from '../../data-actions/tabs/tab-state-data-action';
import TabItem, { getContentID, TabItemType } from '../citta-tab-item/citta-tab-item';
import { ICittaTabsData } from './citta-tabs.data';
import { ICittaTabsProps } from './citta-tabs.props.autogenerated';

/**
 *
 * Tab component
 * @extends {React.PureComponent<ICittaTabsProps<ICittaTabsData>>}
 */

export interface ICallToActionProps {
  link: INodeProps[];
}

export interface ITabViewProps extends ICittaTabsProps<{}> {
  heading?: React.ReactNode;
  TabContainer: IModuleProps;
  headerSection: INodeProps;
  tabHeaderSection: INodeProps;
  tabsSection: INodeProps;
  tabItemContainer: INodeProps;
  tabHeaders: React.ReactNode;
  tabContents: React.ReactNode;
  isMobileView?: boolean;
  renderMobileHeader: React.ReactNode;
  tabMobileHeaders: React.ReactNode;
  headerMobileSection: INodeProps;
}
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export interface ITabViewStates {
  currentTabIndex: number;
  pathName: string;
  selectedIndex: string;
  isMobile: boolean;
  selectedDropDown: string;
  dropDownOpen: boolean;
}
interface IRequestContextUrl {
  requestUrl: URL;
  staticCdnUrl: string;
}
/**
 *
 * Tab component
 * @extends {React.Component<ICittaTabsProps<ICittaTabsData>>}
 */
@observer
class Tab extends React.Component<ICittaTabsProps<ICittaTabsData>, ITabViewStates> {
  public readonly tabIndexQuery: string = 'activeTab';
  private activeLink: HTMLLIElement | null;
  private resizeThrottledEventHandler?: (event: Event) => void;
  @observable
  private viewport: GridSize;

  constructor(props: ICittaTabsProps<ICittaTabsData>) {
    super(props);
    const { defaultTabIndex } = this.props.config;
    const { slots } = this.props;
    const slotLength = slots && slots.tabItems && slots.tabItems.length || 0;
    const initialIndex = defaultTabIndex && defaultTabIndex <= slotLength && defaultTabIndex > 0 ? defaultTabIndex - 1 : 0;
    this.viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
    const selectedDdlName = slots && slots.tabItems && slots.tabItems.length && slots.tabItems[defaultTabIndex! - 1];
    // @ts-ignore
    const defaultSelectedTabTitle: string = selectedDdlName && (selectedDdlName as TabItem).props.config.tabItemTitle || '';
    this.state = { currentTabIndex: initialIndex, pathName: '', selectedIndex: '', selectedDropDown: defaultSelectedTabTitle, isMobile: this.isMobile, dropDownOpen: false };
    this._getTabHeader = this._getTabHeader.bind(this);
    this._getTabContent = this._getTabContent.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._setGetParameter = this._setGetParameter.bind(this);
    this.activeLink = null;
    this._getTabNameFromRequestContextUrl = this._getTabNameFromRequestContextUrl.bind(this);
    this._getRequestContextUrl = this._getRequestContextUrl.bind(this);
    this._getRequestContext = this._getRequestContext.bind(this);
    this._setState = this._setState.bind(this);
    this._getEncodedTabName = this._getEncodedTabName.bind(this);
    this._updateViewport = this._updateViewport.bind(this);
    this._dropDownAction = this._dropDownAction.bind(this);
  }
  @computed get isMobile(): boolean {
    return (this.viewport === 'xs' || this.viewport === 'sm');
  }
  public componentDidMount(): void {
    this._setState();
    this.resizeThrottledEventHandler =
      window && addThrottledEvent(window, 'resize', this._updateViewport as EventListener);
    this._updateViewport();
  }
  public componentWillUnmount(): void {
    window && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
  }
  public render(): JSX.Element {
    const { slots } = this.props;
    const { heading, className } = this.props.config;

    const headingComponent = heading && (
      <MsDyn365.Text
        className='ms-tab-header-section__heading'
        tag={heading.tag || 'h2'}
        text={heading.text}
        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
      />
    );
    const slotLength = slots && slots.tabItems && slots.tabItems.length || 0;
    const renderMobileHeader: React.ReactNode = (
      <h2 className='heading' onClick={this._dropDownAction}>{this.state.selectedDropDown && this.state.selectedDropDown}
        <span className={this.state.dropDownOpen && this.state.dropDownOpen ? 'dropdown-open-icon' : 'dropdown-close-icon'} />
      </h2>
    );
    const tabMobileHeaders: React.ReactNode = (
      <ul role='tablist' className={this.state.dropDownOpen && this.state.dropDownOpen ? 'tabs-list open' : 'tabs-list'}>{slotLength > 0 && slots.tabItems.map((item: React.ReactNode, index: number) => this._getTabHeader(item, index))}</ul>
    );
    const tabViewProps = {
      ...this.props,
      TabContainer: {
        moduleProps: this.props,
        className: classnames('ms-tab', className, this.state.isMobile ? ' mobile-dropdown-view' : '')
      },
      headerSection: { className: 'ms-tab-header-section' },
      headerMobileSection: { className: 'ms-tab-header' },
      tabsSection: { className: 'ms-tab-tabs-section', tag: 'ul' as NodeTag, role: 'tablist' },
      heading: headingComponent,
      config: this.props && this.props.config,
      tabItemContainer: {
        tag: 'div' as NodeTag,
        className: 'ms-tab-content-section',
      },
      tabContents: slotLength > 0 && slots.tabItems.map((item: React.ReactNode, index: number) => this._getTabContent(item, index)),
      tabHeaders: slotLength > 0 && slots.tabItems.map((item: React.ReactNode, index: number) => this._getTabHeader(item, index)),
      isMobileView: this.state.isMobile,
      tabMobileHeaders: tabMobileHeaders,
      renderMobileHeader: renderMobileHeader
    };
    return this.props.renderView(tabViewProps) as React.ReactElement;
  }

  public handleHeadingChange = (event: MsDyn365.ContentEditableEvent) => this.props.config.heading!.text = event.target.value;

  public componentDidUpdate(): void {
    const { data } = this.props;
    const dataActionTabId = data && data.activeTabState && data.activeTabState.result && data.activeTabState.result.tabId;
    if (dataActionTabId) {
      if (this.props.id === dataActionTabId) {
        this.activeLink && this.activeLink.focus();
      }
    }
  }

  // tslint:disable-next-line: cyclomatic-complexity
  private _setState(): void {
    const { defaultTabIndex } = this.props.config;
    const { slots } = this.props;
    const slotLength = slots && slots.tabItems && slots.tabItems.length || 0;
    let initialIndex = defaultTabIndex && defaultTabIndex <= slotLength && defaultTabIndex > 0 ? defaultTabIndex - 1 : 0;
    let queryTabIndex: number = 0;
    const firstTabItem = slots && slots.tabItems && slots.tabItems.length && slots.tabItems[defaultTabIndex! - 1];
    // @ts-ignore
    let selectedTabTitle: string = firstTabItem && (firstTabItem as TabItem).props.config.tabItemTitle || '';
    let selectedTabIndex: string = `${selectedTabTitle}0`;
    const requestContext = this._getRequestContext();
    const tabName = this._getTabNameFromRequestContextUrl(requestContext.url);
    const requestUrl: URL = new URL(requestContext && requestContext.url && requestContext.url.requestUrl && requestContext.url.requestUrl.toString());
    let pathName = requestUrl.pathname;
    const pathNameSplit = pathName.split('/');
    if (slots && slots.tabItems) {
      for (let i = 0; i < slots.tabItems.length; i++) {
        let tileWithoutSpecialCharacters = (slots.tabItems[i] as TabItem).props.config.tabItemTitle.replace('&','and');
        // @ts-ignore
        const tabTitle: string = this._getEncodedTabName(tileWithoutSpecialCharacters);
        if (tabName.toLocaleLowerCase() === tabTitle.toLocaleLowerCase()) {
          selectedTabTitle = tabTitle;
          selectedTabIndex = selectedTabTitle + i.toString();
          queryTabIndex = i;
          pathName = pathNameSplit.slice(0, pathNameSplit.length - 1).join('/');
          break;
        }
      }
    }
    initialIndex = queryTabIndex > 0 && queryTabIndex <= slotLength ? queryTabIndex : initialIndex;
    this.viewport = this.props.context.request && this.props.context.request.device && this.props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
    const selectedDdlName = slots && slots.tabItems && slots.tabItems.length && slots.tabItems[defaultTabIndex! - 1];
    // @ts-ignore
    const defaultSelectedTabTitle: string = selectedDdlName && (selectedDdlName as TabItem).props.config.tabItemTitle || '';
    this.setState({ currentTabIndex: initialIndex, pathName: pathName, selectedIndex: selectedTabIndex, selectedDropDown: defaultSelectedTabTitle, isMobile: this.isMobile, dropDownOpen: false });
  }
  private _updateViewport(): void {
    this.viewport = this._getViewport();
    this.setState({ isMobile: this.isMobile });
  }

  private _getViewport(): GridSize {
    const { context } = this.props;

    // always render in mobile viewport on a mobile device
    if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
      return 'xs';
    }
    if (typeof window !== undefined && window.innerWidth) {
      const gridSettings = context.request.gridSettings;
      if (gridSettings) {
        if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
          return 'xs';
        } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
          return 'sm';
        } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
          return 'md';
        } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
          return 'lg';
        } else {
          return 'xl';
        }
      }
    }
    return 'xs';
  }

  private _getTabHeader(item: React.ReactNode, index: number): React.ReactNode {
    const { data, config } = this.props;
    const activeContentIndex = data && data.activeTabState && data.activeTabState.result && data.activeTabState.result.activeContentIndex;
    const dataActionTabId = data && data.activeTabState && data.activeTabState.result && data.activeTabState.result.tabId;
    const selected = activeContentIndex !== undefined && dataActionTabId === this.props.id ? activeContentIndex === index : this.state.currentTabIndex === index;
    const contentID = getContentID(index, this.props.id);
    // @ts-ignore
    const tabTitle = (item as TabItem).props.config.tabItemTitle;
    const encodedTabTitle = this._getEncodedTabName(tabTitle);
    const requestContext: MsDyn365.IRequestContext = this._getRequestContext();
    const queryStringUrl = this._getRequestContextUrl(requestContext.url, true);
    let to = `${this.state.pathName}/${encodedTabTitle}${queryStringUrl}`.replace('//', '/');
    to = config.deepLink ? to : queryStringUrl;
    const selectedTabClassName = selected ? 'tab-selected' : '';
    return (
      <li
        key={index}
        className={`ms-tab-tabs-section-li ${selectedTabClassName}`}
        // @ts-ignore
        onClick={this._onClick(item as TabItem, index, this.props.id)}
        onKeyDown={this._onKeyDown(index, this.props.id)}
        tabIndex ={-1}
        // tslint:disable-next-line:react-this-binding-issue
        ref={link => { if (index === this.state.currentTabIndex) { this.activeLink = link; } }}
        aria-controls={contentID}
        aria-selected={selected}
        role='tab'
      >
        <React.Fragment key={index}>
          {React.cloneElement(item as React.ReactElement, { tabItemType: TabItemType.TabHeader, isSelected: index === this.state.currentTabIndex, itemIndex: index, tabId: this.props.id })}
        </React.Fragment>
      </li>
    );
  }

  private _dropDownAction(event: React.MouseEvent<HTMLElement>): void {
    this.setState({ dropDownOpen: !this.state.dropDownOpen });
  }

  private _getEncodedTabName(tabName: string): string {
    /** @ts-ignore */
    return encodeURI(tabName.toLocaleLowerCase().replace(' ', '-').replace(' ', '-'));
  }
  private _getRequestContextUrl(requestContextUrl?: IRequestContextUrl, onlyQueryString?: boolean): string {
    const pageUrl = requestContextUrl && requestContextUrl.requestUrl && requestContextUrl.requestUrl.toString();
    if (onlyQueryString) {
      const requestContextQueryString = pageUrl && pageUrl.split('?');
      const requestContextQueryStringUrl = requestContextQueryString && requestContextQueryString.length > 1 && (requestContextQueryString[1] !== undefined) && requestContextQueryString[1] || '';
      return requestContextQueryStringUrl === '' ? requestContextQueryStringUrl : `?${requestContextQueryStringUrl}`;
    }
    return pageUrl || '';
  }
  private _getRequestContext(): MsDyn365.IRequestContext {
    const { context } = this.props;
    return context && context.actionContext && context.actionContext.requestContext;
  }

  private _getTabNameFromRequestContextUrl(url?: IRequestContextUrl): string {
    const pageUrl = this._getRequestContextUrl(url).split('?');
    const pagePath = pageUrl && pageUrl.length && pageUrl[0].split('/');
    return pagePath && pagePath.length && pagePath[pagePath.length - 1] || '';
  }

  private _getToUrl(item: TabItem, encodedTabTitle: string, requestContext: MsDyn365.IRequestContext): string {
    const queryStringUrl = this._getRequestContextUrl(requestContext.url, true);
    let to = `${this.state.pathName}/${encodedTabTitle}${queryStringUrl}`.replace('//', '/');
    to = this.props.config?.deepLink ? to : queryStringUrl;
    return to;
  }

  private _onClick = (item: TabItem, index: number, id: string) => (e: React.MouseEvent<HTMLLIElement>) => {
    const activeTabState = { activeContentIndex: index, tabId: id };
    const tabTitle = (item as TabItem).props.config.tabItemTitle.replace('&', 'and');
    const encodedTabTitle = this._getEncodedTabName(tabTitle);
    const requestContext: MsDyn365.IRequestContext = this._getRequestContext();
    const toUrl = this._getToUrl(item, encodedTabTitle, requestContext);
    this.props.context.actionContext.update(createTabStateInput(activeTabState), activeTabState);
    requestContext && window.history.pushState({ path: toUrl }, '', toUrl);
    this.setState({ currentTabIndex: index, selectedDropDown: tabTitle, dropDownOpen: false });

  }

  private _getTabTitleByIndex = (index: number) => {
    const { slots } = this.props;
    const selTabItem = slots && slots.tabItems && slots.tabItems.length && slots.tabItems[index!];
    // @ts-ignore
    const selectedTabTitleByIndex = selTabItem && (selTabItem as TabItem).props.config.tabItemTitle || '';
    return { selTabItem, selectedTabTitleByIndex };
  }

  private _onKeyDown = (index: number, id: string) => (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.keyCode === 13) {
      const activeTabState = { activeContentIndex: index, tabId: id };
      this.props.context.actionContext.update(createTabStateInput(activeTabState), activeTabState);
      const seltab = this._getTabTitleByIndex(index);
      const encodedTabTitle = this._getEncodedTabName(seltab.selectedTabTitleByIndex);
      const requestContext: MsDyn365.IRequestContext = this._getRequestContext();
      // @ts-ignore
      const toUrl = this._getToUrl(seltab.selTabItem as TabItem, encodedTabTitle, requestContext);
      this.setState({ currentTabIndex: index, selectedDropDown: seltab.selectedTabTitleByIndex, dropDownOpen: false });
      requestContext && window.history.pushState({ path: toUrl }, '', toUrl);
    }
  }

  private _getTabContent(item: React.ReactNode, index: number): React.ReactNode {
    return (
      <React.Fragment key={index}>
        {React.cloneElement(item as React.ReactElement, { tabItemType: TabItemType.TabContent, isSelected: index === this.state.currentTabIndex, itemIndex: index, tabId: this.props.id })}
      </React.Fragment>
    );
  }

  private _setGetParameter(requestContext: MsDyn365.IRequestContext, paramName: string, paramValue: string): string {
    let url;

    if (MsDyn365.msdyn365Commerce.isBrowser) {
      url = new URL(window.location.href);
    } else {
      url = new URL(requestContext.url.requestUrl.href);
    }
    const params = new window.URLSearchParams(url.search);
    params.set(paramName, paramValue);
    url.search = decodeURIComponent(params.toString());
    return url.toString();
  }
}

export default Tab;